import { useState } from "react";
import {
  Flex,
  Paper,
  Text,
  Title,
  Modal,
  Button,
  FileInput,
  Stack,
  SegmentedControl,
} from "@mantine/core";
import { CVIcon, EditIcon } from "../../shared/svg";
import "./Profile.scss";

import useAuthStore from "../../store/useAuthStore";
import { useTranslation } from "react-i18next";
import useProfileStore from "../../store/useProfileStore";
import useReportStore from "../../store/useReportStore";
import useCoursesStore from "../../store/useCoursesStore";
import { OnboardingTour } from "@gfazioli/mantine-onboarding-tour";
import { useMediaQuery } from "@mantine/hooks";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export const CVBlock = () => {
  const [opened, setOpened] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const { access_token, onProcessingCv, cvProcessing } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const { profile, setChanges, resetProfileFields, uploadProfileCv } =
    useProfileStore();
  const { resetReportState } = useReportStore();
  const { resetRecommendedCourses } = useCoursesStore();
  const [loading, setLoading] = useState(false);
  const [replacePersonalData, setReplacePersonalData] = useState(false);
  const [changePersonalInfo, setChangePersonalInfo] = useState(false);

  const mobile = useMediaQuery("(max-width: 650px)");

  const url = process.env.REACT_APP_BACKEND_URL;
  const cleanedUrl = url?.endsWith("/") ? url.slice(0, -1) : url;

  const cvFile = profile?.user_cv?.file;

  const cvUrl = cvFile?.startsWith("http")
    ? cvFile
    : `${cleanedUrl}${cvFile ?? ""}`;

  const fileName = profile?.user_cv?.file_name?.replace(/_/g, " ") ?? null;

  const handleFileChange = (file: File | null) => {
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setError(t("fileTooLarge"));
        setFile(null);
      } else {
        setError(null);
        setFile(file);
      }
    }
  };
  const handleSubmit = async () => {
    if (file && access_token) {
      try {
        setLoading(true);
        const response = await uploadProfileCv({ cvFile: file });

        if (response) {
          await onProcessingCv({
            cvId: response.data.id,
            replace_personal_data: changePersonalInfo,
            replace_profile_data: replacePersonalData,
          });
          resetProfileFields();
          resetReportState();
          resetRecommendedCourses();
          setChanges(false);
          toggleModal();
        }

        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        console.log(error);

        setError(error.response.data.error);
      }
    }
  };

  const toggleModal = () => setOpened((prev) => !prev);

  return (
    <>
      {mobile ? (
        <Paper withBorder radius="24px" p={32} h="100%" pos="relative">
          <div className="edit-icon" onClick={toggleModal}>
            <EditIcon />
          </div>
          <OnboardingTour.Target id="profile-cv">
            <Flex direction="column" justify="center" align="center" h="100%">
              <CVIcon />
              <Title size={24} mt={16}>
                {t("yourCV")}
              </Title>
              <Text mt={8} ta="center" style={{ wordBreak: "break-word" }}>
                {fileName ? (
                  <a href={cvUrl} target="_blank" rel="noopener noreferrer">
                    {fileName}
                  </a>
                ) : (
                  <span style={{ color: "gray", fontSize: "14px" }}>
                    {t("noCvUploaded")}
                  </span>
                )}
              </Text>
              {cvProcessing === "failed" && (
                <Text color="#B00020" style={{ textAlign: "center" }} mt={4}>
                  {t("failed_cv")}
                </Text>
              )}
            </Flex>
          </OnboardingTour.Target>
        </Paper>
      ) : (
        <OnboardingTour.Target id="profile-cv">
          <Paper withBorder radius="24px" p={32} h="100%" pos="relative">
            <div className="edit-icon" onClick={toggleModal}>
              <EditIcon />
            </div>
            <Flex direction="column" justify="center" align="center" h="100%">
              <CVIcon />
              <Title size={24} mt={16}>
                {t("yourCV")}
              </Title>
              <Text mt={8} ta="center" style={{ wordBreak: "break-word" }}>
                {fileName ? (
                  <a href={cvUrl} target="_blank" rel="noopener noreferrer">
                    {fileName}
                  </a>
                ) : (
                  <span style={{ color: "gray", fontSize: "14px" }}>
                    {t("noCvUploaded")}
                  </span>
                )}
              </Text>
              {cvProcessing === "failed" && (
                <Text color="#B00020" style={{ textAlign: "center" }} mt={4}>
                  {t("failed_cv")}
                </Text>
              )}
            </Flex>
          </Paper>
        </OnboardingTour.Target>
      )}
      <Modal
        zIndex={500}
        opened={opened}
        onClose={() => {
          toggleModal();
          setFile(null);
          setReplacePersonalData(false);
          setChangePersonalInfo(false);
        }}
        centered
        title={
          <Text fw={600} fz="lg">
            {t("cvModal.title")}
          </Text>
        }
        size="md"
      >
        <Stack gap="md">
          <Text fz="sm" fw={500}>
            {t("cvModal.description")}
          </Text>

          <Text fz="xs" c="dimmed" mt={-8}>
            {t("cvModal.note")}
          </Text>

          <Text fz="sm" fw={600} mt="sm">
            {t("cvModal.chooseLabel")}
          </Text>

          {/* Personal Information Section */}
          <div style={{ width: "100%" }}>
            <Text fz="sm" fw={500} mb={4}>
              {t("cvModal.personalInfo")}
            </Text>
            <SegmentedControl
              value={changePersonalInfo ? "replace" : "keep"}
              onChange={(val) => setChangePersonalInfo(val === "replace")}
              data={[
                { label: t("cvModal.keep"), value: "keep" },
                { label: t("cvModal.replace"), value: "replace" },
              ]}
              radius="md"
              size="sm"
              color="violet"
              style={{ width: "100%" }}
            />
          </div>

          {/* Profile Data Section */}
          <div style={{ width: "100%" }}>
            <Text fz="sm" fw={500} mb={4}>
              {t("cvModal.profileData")}
            </Text>
            <SegmentedControl
              value={replacePersonalData ? "replace" : "extend"}
              onChange={(val) => setReplacePersonalData(val === "replace")}
              data={[
                { label: t("cvModal.extend"), value: "extend" },
                { label: t("cvModal.replace"), value: "replace" },
              ]}
              radius="md"
              size="sm"
              color="violet"
              style={{ width: "100%" }}
            />
          </div>

          <FileInput
            label={t("selectFile")}
            placeholder={t("uploadFile")}
            accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, text/plain"
            onChange={handleFileChange}
            size="md"
            disabled={loading}
          />

          {error && (
            <Text size="sm" color="red">
              {error}
            </Text>
          )}

          {loading && (
            <Text size="sm" color="gray">
              {t("wait_cv_check")}
            </Text>
          )}

          <Button
            fullWidth
            size="md"
            mt="md"
            onClick={() => {
              if (file) {
                handleSubmit();
              } else {
                console.log(t("fileNotSelected"));
              }
            }}
            disabled={!file}
            loading={loading}
          >
            {t("uploadButton")}
          </Button>
        </Stack>
      </Modal>
    </>
  );
};
